/**
 * Real client for /api/v1/affiliate/* (หลังบ้านพาร์ทเนอร์, owner + head — api/app/affiliate/routes.py): same origin +
 * the session cookie. Writes carry the Idempotency-Key the page made (a retry after a network error sends the same
 * key, so "อนุมัติ" twice is still one approval).
 * Errors become AffError{status, reason, message}: the affiliate's {"detail": {"reason", "message", …}} keeps its
 * reason and extra fields (`extra`: suggestion, in_use, fields); the chat's own {"detail": "…"} is the message.
 * Only a 401 signs out — and the gateway never passes the affiliate's 401 on (that one arrives as 502 affAuth).
 */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type {
  AdminDoc,
  AdminOrderRow,
  AdminPayout,
  AdminPayoutRow,
  AffiliateApi,
  AffStatus,
  ApplicationsQuery,
  AppRow,
  ApproveBody,
  Campaign,
  CampaignIn,
  ClaimBody,
  CreatePartnerBody,
  MarkPaidBody,
  Overview,
  OverviewType,
  Page,
  Partner,
  PartnerPatch,
  PartnerRow,
  PartnersQuery,
  PayoutPage,
  PayoutsQuery,
  PlaceCheck,
  RequestDocsBody,
  ShopOrderDetail,
  ShopOrderRow,
  ShopOrdersView,
  SlipBody,
  SlugCheck,
  Summary,
  Template,
  TemplateBody,
  TemplateSaveBody,
  TemplateSaveResult,
  TierChange,
} from './types'

const THAI = /[฀-๿]/

export class AffError extends ApiError {
  constructor(
    status: number,
    readonly reason: string,
    message: string,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(status, message)
    this.name = 'AffError'
  }
}

function fallback(status: number): string {
  if (status === 403) return 'หน้านี้เปิดได้เฉพาะเจ้าของและหัวหน้า'
  if (status === 404) return 'ไม่พบข้อมูลนี้ในระบบพาร์ทเนอร์'
  return `ทำรายการไม่สำเร็จ (รหัส ${status}) ลองใหม่อีกครั้ง`
}

/** AffError from an error answer's body (whatever shape it has) */
export function affErrorOf(status: number, body: unknown): AffError {
  const detail = (body as { detail?: unknown } | null)?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const d = detail as Record<string, unknown>
    const reason = typeof d.reason === 'string' ? d.reason : ''
    const message = typeof d.message === 'string' && THAI.test(d.message) ? d.message : fallback(status)
    return new AffError(status, reason, message, d)
  }
  if (typeof detail === 'string' && THAI.test(detail)) return new AffError(status, '', detail)
  return new AffError(status, '', fallback(status))
}

type Query = Record<string, string | number | null | undefined>
/** a slip (≤ 2.5 MB, a 3.3 MB request) from a phone on a slow uplink needs longer than the page's 15 s (review M4) */
export const SLIP_TIMEOUT_MS = 60_000
/** the month's 50 ทวิ: the gateway waits up to 60 s for the affiliate's ≤ 200 pages, then the transfer (review M5) */
export const MONTH_PDF_TIMEOUT_MS = 90_000

export class HttpAffiliateApi implements AffiliateApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private path(p: string): string {
    return `${this.base.replace(/\/+$/, '')}/api/v1/affiliate${p}`
  }

  private url(p: string, q?: Query): string {
    const u = new URL(this.path(p), window.location.origin)
    for (const [k, v] of Object.entries(q ?? {})) {
      if (v !== null && v !== undefined && v !== '') u.searchParams.set(k, String(v))
    }
    return u.toString()
  }

  private async req<T>(
    method: string,
    p: string,
    opts: { query?: Query; body?: unknown; idemKey?: string; timeoutMs?: number; blob?: string } = {},
  ): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs)
    const headers: Record<string, string> = { Accept: opts.blob ?? 'application/json' }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    if (opts.idemKey) headers['Idempotency-Key'] = opts.idemKey
    try {
      const res = await this.fetchImpl(this.url(p, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.json()
        } catch {
          /* not JSON (a proxy page) */
        }
        throw affErrorOf(res.status, body)
      }
      if (opts.blob) return (await res.blob()) as T
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof AffError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new AffError(0, 'timeout', 'ระบบไม่ตอบในเวลาที่กำหนด ลองใหม่อีกครั้ง')
      }
      throw new AffError(0, 'network', 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง')
    } finally {
      clearTimeout(timer)
    }
  }

  status(): Promise<AffStatus> {
    return this.req('GET', '/status')
  }
  whoami(): Promise<unknown> {
    return this.req('GET', '/whoami')
  }
  summary(): Promise<Summary> {
    return this.req('GET', '/summary')
  }
  applications(q: ApplicationsQuery): Promise<Page<AppRow>> {
    return this.req('GET', '/applications', { query: { status: q.status, limit: q.limit, offset: q.offset } })
  }
  application(id: number): Promise<Partner> {
    return this.req('GET', `/applications/${id}`)
  }
  approve(id: number, body: ApproveBody, idemKey: string): Promise<Partner> {
    return this.req('POST', `/applications/${id}/approve`, { body, idemKey })
  }
  reject(id: number, reason: string, idemKey: string): Promise<Partner> {
    return this.req('POST', `/applications/${id}/reject`, { body: { reason }, idemKey })
  }
  createPartner(body: CreatePartnerBody, idemKey: string): Promise<Partner> {
    return this.req('POST', '/partners', { body, idemKey })
  }
  partners(q: PartnersQuery): Promise<Page<PartnerRow>> {
    return this.req('GET', '/partners', {
      query: { type: q.type, status: q.status, q: q.q, parent_gym_id: q.parent_gym_id, limit: q.limit, offset: q.offset },
    })
  }
  partner(id: number): Promise<Partner> {
    return this.req('GET', `/partners/${id}`)
  }
  patchPartner(id: number, patch: PartnerPatch, idemKey: string): Promise<Partner> {
    return this.req('PATCH', `/partners/${id}`, { body: patch, idemKey })
  }
  setSlug(id: number, slug: string, idemKey: string): Promise<Partner> {
    return this.req('PUT', `/partners/${id}/slug`, { body: { slug }, idemKey })
  }
  setTiers(id: number, change: TierChange, idemKey: string): Promise<Partner> {
    return this.req('PUT', `/partners/${id}/tiers`, { body: change, idemKey })
  }
  checkSlug(slug: string, partnerId?: number | null): Promise<SlugCheck> {
    return this.req('GET', '/slugs/check', { query: { slug, partner_id: partnerId ?? null } })
  }
  templates(archived = false): Promise<{ items: Template[] }> {
    return this.req('GET', '/tier-templates', { query: { archived: archived ? 1 : null } })
  }
  createTemplate(body: TemplateBody, idemKey: string): Promise<Template> {
    return this.req('POST', '/tier-templates', { body, idemKey })
  }
  saveTemplate(id: number, body: TemplateSaveBody, idemKey: string): Promise<TemplateSaveResult> {
    return this.req('PUT', `/tier-templates/${id}`, { body, idemKey })
  }
  archiveTemplate(id: number, idemKey: string): Promise<Template> {
    return this.req('DELETE', `/tier-templates/${id}`, { idemKey })
  }
  campaigns(archived = false): Promise<{ items: Campaign[] }> {
    return this.req('GET', '/campaigns', { query: { archived: archived ? 1 : null } })
  }
  createCampaign(body: CampaignIn, idemKey: string): Promise<Campaign> {
    return this.req('POST', '/campaigns', { body, idemKey })
  }
  updateCampaign(id: number, body: CampaignIn, idemKey: string): Promise<Campaign> {
    return this.req('PUT', `/campaigns/${id}`, { body, idemKey })
  }
  archiveCampaign(id: number, idemKey: string): Promise<Campaign> {
    return this.req('DELETE', `/campaigns/${id}`, { idemKey })
  }
  shopOrders(view?: ShopOrdersView, limit?: number, offset?: number): Promise<Page<ShopOrderRow>> {
    return this.req('GET', '/shop-orders', { query: { view, limit, offset } })
  }
  retryShopOrder(id: number, idemKey: string): Promise<ShopOrderRow> {
    return this.req('POST', `/shop-orders/${id}/retry`, { body: {}, idemKey })
  }
  resolveShopOrder(id: number, note: string, idemKey: string): Promise<ShopOrderRow> {
    return this.req('POST', `/shop-orders/${id}/resolve`, { body: note ? { note } : {}, idemKey })
  }
  // ---- P4: payouts, documents, overview, claims
  payouts(q: PayoutsQuery): Promise<PayoutPage> {
    return this.req('GET', '/payouts', { query: { view: q.view, month: q.month, limit: q.limit, offset: q.offset } })
  }
  payout(id: number): Promise<AdminPayout> {
    return this.req('GET', `/payouts/${id}`)
  }
  uploadSlip(body: SlipBody, idemKey: string): Promise<{ slip_id: string }> {
    return this.req('POST', '/payouts/slips', { body, idemKey, timeoutMs: SLIP_TIMEOUT_MS })
  }
  markPaid(body: MarkPaidBody, idemKey: string): Promise<{ items: AdminPayoutRow[] }> {
    return this.req('POST', '/payouts/mark-paid', { body, idemKey })
  }
  rejectPayout(id: number, reason: string, idemKey: string): Promise<AdminPayoutRow> {
    return this.req('POST', `/payouts/${id}/reject`, { body: { reason }, idemKey })
  }
  requestDocs(id: number, body: RequestDocsBody, idemKey: string): Promise<AdminPayoutRow> {
    return this.req('POST', `/payouts/${id}/request-docs`, { body, idemKey })
  }
  confirmDocs(id: number, docIds: number[], idemKey: string): Promise<AdminPayoutRow> {
    return this.req('POST', `/payouts/${id}/confirm-docs`, { body: { doc_ids: docIds }, idemKey })
  }
  monthWhtFile(month: string): Promise<Blob> {
    return this.req('GET', '/payouts/wht.pdf', { query: { month }, timeoutMs: MONTH_PDF_TIMEOUT_MS, blob: 'application/pdf' })
  }
  partnerDocuments(id: number): Promise<{ items: AdminDoc[] }> {
    return this.req('GET', `/partners/${id}/documents`)
  }
  overview(month?: string | null, type?: OverviewType): Promise<Overview> {
    return this.req('GET', '/overview', { query: { month, type: type === 'all' ? null : type } })
  }
  partnerOrders(id: number, limit?: number, offset?: number): Promise<Page<AdminOrderRow>> {
    return this.req('GET', `/partners/${id}/orders`, { query: { limit, offset } })
  }
  shopOrder(id: number): Promise<ShopOrderDetail> {
    return this.req('GET', `/shop-orders/${id}`)
  }
  claim(id: number, body: ClaimBody, idemKey: string): Promise<ShopOrderDetail> {
    return this.req('POST', `/shop-orders/${id}/claims`, { body, idemKey })
  }
  // ---- P5: the gym's Lalamove zone (box days / DHL address ride on patchPartner and approve)
  placeCheck(id: number): Promise<PlaceCheck> {
    return this.req('GET', `/partners/${id}/place-check`)
  }
  photoUrl(id: number): string {
    return this.path(`/applications/${id}/photo`)
  }
  qrUrl(id: number): string {
    return this.path(`/partners/${id}/qr.svg`)
  }
  slipUrl(id: number): string {
    return this.path(`/payouts/${id}/slip`)
  }
  documentUrl(id: number): string {
    return this.path(`/documents/${id}/file`)
  }
  whtPdfUrl(id: number): string {
    return this.path(`/payouts/${id}/wht.pdf`)
  }
  monthWhtUrl(month: string): string {
    return this.path(`/payouts/wht.pdf?month=${encodeURIComponent(month)}`)
  }
  exportCsvUrl(month: string): string {
    return this.path(`/payouts/export.csv?month=${encodeURIComponent(month)}`)
  }
}
